<template>
  <div
    :style="cssProps"
    ref="divRef"
  >
    <MaterialPreview
      v-model="materialPreviewId"
      :material="selectedMaterialForPreview"
      :show-upselling="showVideoUpsell"
      :is-image="false"
    />
    <DeleteScheduledPost
      v-if="deleteScheduledPostDialog"
      :content="contentForDelete"
      @postDeleted="$emit('savedScheduledContent')"
      @scheduledEmailsDeleted="
        ($emit('savedScheduledContent'),
        (scheduledEmailsDeleted = true),
        (deleteScheduledPostDialog = false))
      "
      @close="deleteScheduledPostDialog = false"
    />
    <EditContent
      v-if="showEditContentDialog"
      :content="selectedContentForEdit"
      :kind="selectedContentForEdit?.textKind"
      :topic="selectedContentForEdit?.slug"
      @update="updateScheduledPostText"
      @close="closeEditDialog"
    />
    <v-dialog
      v-model="showViewDetailsDialog"
      :width="isNewsletter ? '80vw' : 1100"
      :fullscreen="$vuetify.display.smAndDown"
      content-class="social-media-dialog"
    >
      <v-card class="social-media-dialog d-flex flex-column">
        <v-card-title class="pa-0">
          <v-toolbar
            color="primary"
            dark
            flat
          >
            <v-toolbar-title class="dialog-title text-white">
              {{
                isNewsletter
                  ? $t('content-creator.newsletter-details.title')
                  : $t('content-creator.post-details')
              }}
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn
                icon
                @click="closeViewDetailsDialog"
              >
                <v-icon
                  size="large"
                  class="text-white"
                >
                  mdi-close
                </v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>

        <div
          v-if="batchPostsLoading"
          class="preview-step-wrapper d-flex justify-center align-center pa-8"
        >
          <ProgressCircular />
        </div>
        <div
          v-else-if="selectedContentForView"
          :class="{
            'preview-step-wrapper': true,
            'preview-step-wrapper__newsletter': isNewsletter,
          }"
        >
          <PreviewStep
            :is-loading-offices="loadingPreviewStep"
            :content="resolvedContent"
            :offices="resolvedOffices"
            :show-post-column="resolvedShowPostColumn"
            :submit-text="resolvedSubmitText"
            :show-back-button="false"
            :is-scheduled-post="!!selectedContentForView?.scheduledAt || false"
            :hide-submit-button="true"
            :custom-statistics="isNewsletter ? newsletterStatistics : undefined"
            :preview-title="resolvedPreviewTitle"
            :table-title="
              isNewsletter
                ? $t('content-creator.newsletter-details.preview-table-newsletter-title')
                : undefined
            "
            :second-column-title="
              isNewsletter ? $t('content-creator.newsletter-details.table.recipients') : undefined
            "
            :is-newsletter-mode="isNewsletter"
            :is-posting="!isNewsletter ? false : false"
            :disabled="!isNewsletter ? true : false"
            :is-history="!isNewsletter"
            :newsletter-history-recipient-mode="isNewsletter && !isMasterView"
            :show-newsletter-delivery-column="isNewsletter && isMasterView"
            :page="newsletterRecipientsPage"
            :items-length="newsletterTableItemsLength"
            :items-per-page="newsletterRecipientsPerPage"
            @update:page="newsletterRecipientsPage = $event"
            @update:items-per-page="onNewsletterTableItemsPerPage"
          />
        </div>
      </v-card>
    </v-dialog>
    <v-window-item :value="filterValue">
      <BlurredBackdropUpselling :is-blurred="disabled">
        <v-timeline
          align="center"
          justify="auto"
          :hide-opposite="isMobile"
          :side="isMobile ? 'end' : undefined"
        >
          <v-timeline-item
            :width="!isMobile ? $refs.divRef?.offsetWidth / 2.1 + 'px' : '100%'"
            :hide-dot="isMobile"
            :line-inset="isMobile ? 0 : ''"
            :class="{ 'mb-4': savedContents.length === 0 }"
          >
            <template #icon>
              <v-icon
                color="white"
                start
                style="margin: 0"
              >
                mdi mdi-alert-circle
              </v-icon>
            </template>
            <v-card
              class="elevation-2 card-create-more-posts"
              elevation="2"
            >
              <v-btn
                color="primary"
                elevation="0"
                :style="{ 'font-size': 12 + 'px' }"
                class="ma-4"
                @click="activateContentCreatorTab"
              >
                <v-icon
                  class="mr-2"
                  :style="{ 'font-size': $vuetify.display.smAndDown ? 12 + 'px' : 14 + 'px' }"
                >
                  mdi mdi-brain
                </v-icon>
                {{ $t('buttons.content-creator.create-more-posts') }}
              </v-btn>
            </v-card>
          </v-timeline-item>

          <v-timeline-item
            v-for="content in savedContents"
            :key="content.id"
            :width="!isMobile ? $refs.divRef?.offsetWidth / 2.1 + 'px' : '100%'"
            :hide-dot="isMobile"
            :line-inset="isMobile ? 0 : ''"
            class="v-timeline-item"
          >
            <template
              v-if="contentType === ContentType.SAVED_CONTENT"
              v-slot:icon
            >
              <v-avatar>
                <r-img
                  v-if="KindTypeIcon[content.textKind]"
                  :src="KindTypeIcon[content.textKind]"
                  class="timeline-avatar"
                  cover
                />
                <span
                  v-else
                  class="avatar-icon-download mdi mdi-download"
                />
              </v-avatar>
            </template>
            <template
              v-else
              v-slot:icon
            >
              <v-icon
                color="white"
                start
                style="margin: 0"
              >
                mdi mdi-clock-time-five
              </v-icon>
            </template>
            <template #opposite>
              <span v-if="contentType === ContentType.SAVED_CONTENT">
                <span class="createdAt"
                  >{{
                    content.textKind === PostType.newsletter
                      ? $t('content-creator.email-sent-on')
                      : $t('content-creator.created-on')
                  }}:</span
                >
                <p class="createdAt-content">
                  {{
                    contentType.textKind === PostType.newsletter
                      ? content.sentAt
                      : formatDateWithWeekdayName(content.postedAt, $i18n.locale)
                  }}
                </p>
              </span>
              <span v-else>
                <span class="createdAt"
                  >{{
                    content.textKind === PostType.newsletter
                      ? $t('content-creator.email-scheduled-for')
                      : $t('content-creator.scheduled-for')
                  }}:</span
                >
                <p class="createdAt-content scheduled-for">
                  {{
                    formatDateWithWeekdayName(
                      content.socialMediaPosts[0]?.scheduledAt ?? content.scheduledAt,
                      $i18n.locale
                    )
                  }}
                </p>
              </span>
              <div
                v-if="showViewDetailsButton(content)"
                class="master-view-info mt-3"
              >
                <div class="d-flex align-center flex-wrap gap-2">
                  <div
                    v-if="content.textKind !== PostType.newsletter && getPlatformIcon(content)"
                    class="d-flex align-center"
                  >
                    <v-avatar
                      size="24"
                      class="mr-1"
                    >
                      <r-img
                        :src="getPlatformIcon(content)"
                        cover
                      />
                    </v-avatar>
                    <span class="text-primary font-weight-medium">
                      {{ getPlatformName(content) }}
                    </span>
                  </div>
                  <div
                    v-if="getOfficesCount(content) > 0"
                    class="d-flex align-center"
                  >
                    <v-icon
                      size="20"
                      class="mr-1 text-primary"
                    >
                      mdi-office-building-outline
                    </v-icon>
                    <span class="text-primary font-weight-medium">
                      {{
                        $t('content-creator.preview-step.offices', { n: getOfficesCount(content) })
                      }}
                    </span>
                  </div>
                  <div
                    v-if="getAccountsCount(content) > 0"
                    class="d-flex align-center"
                  >
                    <v-icon
                      size="20"
                      class="mr-1 text-primary"
                    >
                      mdi-badge-account-horizontal-outline
                    </v-icon>
                    <span class="text-primary font-weight-medium">
                      {{
                        $t('content-creator.preview-step.accounts', {
                          n: getAccountsCount(content),
                        })
                      }}
                    </span>
                  </div>
                  <div
                    v-if="content.textKind === PostType.newsletter"
                    class="d-flex align-center text-primary"
                  >
                    <v-icon
                      size="20"
                      color="primary"
                      class="mr-1 text-primary"
                    >
                      mdi-badge-account-horizontal-outline
                    </v-icon>
                    <span class="text-primary font-weight-medium">
                      {{ $t('content-creator.recipients', { n: getRecipientsCount(content) }) }}
                    </span>
                  </div>
                </div>
                <v-btn
                  color="primary"
                  size="small"
                  class="mt-2 view-details-btn"
                  @click="openViewDetailsDialog(content)"
                >
                  {{ $t('content-creator.view-details') }}
                </v-btn>
              </div>
            </template>
            <v-card elevation="2">
              <div class="d-md-flex flex-md-no-wrap justify-md-space-between">
                <div>
                  <v-card-title class="text-h5 px-2 px-sm-4 pb-0 pb-sm-4">
                    {{
                      $t(
                        `content-creator.text-kind-types.${transformToKebabCase(content.textKind)}`
                      )
                    }}
                    <div
                      v-if="content.carouselImages && content.carouselImages.length > 1"
                      class="carousel-indicator-badge"
                    >
                      <v-icon
                        size="20"
                        color="white"
                      >
                        mdi-view-carousel
                      </v-icon>
                      <span class="carousel-count">{{ content.carouselImages.length }}</span>
                    </div>
                  </v-card-title>
                  <div
                    v-if="
                      (content.downloaded || content.socialMediaPosts?.length > 0) &&
                      content.textKind !== PostType.whatsappStatus
                    "
                    class="px-2 px-sm-4 pb-2 d-flex align-center flex-wrap"
                  >
                    <span
                      v-if="contentType === ContentType.SAVED_CONTENT"
                      class="mr-1"
                    >
                      <span v-if="content.downloaded || content.socialMediaPosts?.length > 0"
                        >{{ $t('content-creator.table.labels.posted-on') }}:</span
                      >
                    </span>
                    <span
                      v-else
                      class="mr-1"
                    >
                      {{ $t('content-creator.table.labels.scheduled-for') }}:
                    </span>
                    <v-tooltip
                      v-for="post in content.socialMediaPosts"
                      :key="post.id"
                      location="bottom"
                    >
                      <template v-slot:activator="{ props }">
                        <component
                          :is="post.permalink ? 'a' : 'span'"
                          :href="post.permalink ? 'javascript:void(0);' : undefined"
                          @click="post.permalink && onSocialMediaIconClick(post.permalink)"
                        >
                          <div
                            class="social-post-icon-wrapper mr-1 mr-md-2 mb-1 mb-sm-0"
                            v-bind="props"
                          >
                            <v-avatar
                              :size="$vuetify.display.smAndDown ? 20 : 25"
                              tile
                            >
                              <r-img :src="KindTypeIcon[TypeToKind[post.type]]" />
                            </v-avatar>
                            <v-icon
                              v-if="post.hasFailed"
                              class="post-status-badge"
                              size="20"
                              color="error"
                              >mdi-alert-circle</v-icon
                            >
                            <v-icon
                              v-else-if="
                                !post.completedAt && !post.externalPostId && !post.permalink
                              "
                              class="post-status-badge"
                              size="20"
                              color="grey-darken-1"
                              >mdi-clock-outline</v-icon
                            >
                          </div>
                        </component>
                      </template>
                      <span
                        >{{ platformName(post.type) }} —
                        {{ getSocialMediaPostStatusLabel(post) }}</span
                      >
                    </v-tooltip>

                    <v-tooltip
                      v-if="content.downloaded"
                      location="bottom"
                    >
                      <template v-slot:activator="{ props }">
                        <v-icon
                          size="25"
                          color="white"
                          class="square-icon"
                          v-bind="props"
                        >
                          mdi-download
                        </v-icon>
                      </template>
                      <span class="mr-1 mr-sm-2">{{
                        $t('content-creator.table.labels.downloaded')
                      }}</span>
                    </v-tooltip>
                  </div>
                  <Feature :featureSlug="featureNames.POST_METRICS">
                    <PostMetricsRow
                      v-if="contentType === ContentType.SAVED_CONTENT"
                      :posts="content.socialMediaPosts"
                    />
                  </Feature>
                  <v-tooltip
                    class="tooltip-compact"
                    content-class="tooltip-class"
                    location="top"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props">
                        <v-card-text
                          role="button"
                          tabindex="0"
                          :aria-disabled="disabled || contentType !== ContentType.SCHEDULED_CONTENT"
                          data-product-fruits="scheduled-text-edit-trigger"
                          class="pa-2 pa-sm-4"
                          :class="{
                            'clickable-text': contentType === ContentType.SCHEDULED_CONTENT,
                          }"
                          @click="
                            contentType === ContentType.SCHEDULED_CONTENT && !disabled
                              ? openEditDialog(content)
                              : null
                          "
                          @keydown.enter.prevent="
                            contentType === ContentType.SCHEDULED_CONTENT && !disabled
                              ? openEditDialog(content)
                              : null
                          "
                          @keydown.space.prevent="
                            contentType === ContentType.SCHEDULED_CONTENT && !disabled
                              ? openEditDialog(content)
                              : null
                          "
                        >
                          <span
                            v-if="content.textKind === PostType.newsletter"
                            v-html="htmlTruncateAndReplace(content.text, 150)"
                          />
                          <span v-else>{{ textTruncate(content.text, 150) }}</span>
                        </v-card-text>
                      </span>
                    </template>

                    <span class="d-flex justify-space-between tooltip-content">
                      <v-alert
                        class="mt-5 alert"
                        icon="mdi-information-slab-circle"
                        type="info"
                        color="primary"
                      >
                        <span
                          v-if="content.textKind === PostType.newsletter"
                          v-html="htmlReplace(content.text)"
                        />
                        <span v-else>{{ content.text }}</span>
                      </v-alert>
                    </span>
                  </v-tooltip>
                  <div
                    v-if="!isMobile && content.textKind === PostType.whatsappStatus"
                    class="px-2 px-sm-4 pb-4"
                  >
                    <div class="whatsapp-qr-wrapper">
                      <WhatsAppQr :url="whatsAppQrUrl(content)" />
                    </div>
                  </div>
                </div>
                <div
                  v-if="isVideo(content) && content.textKind === PostType.whatsappStatus"
                  class="ma-2 ma-sm-4 whatsapp-video-preview-container"
                  @click="openVideoPreview(content)"
                >
                  <video
                    :src="getMediaUrl(content)"
                    class="whatsapp-video-preview"
                    muted
                    preload="metadata"
                    @loadedmetadata="onVideoLoaded"
                  />
                  <div class="video-play-overlay">
                    <div class="video-indicator">
                      <v-icon
                        color="white"
                        size="48"
                      >
                        mdi-play-circle
                      </v-icon>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="isVideo(content)"
                  class="ma-2 ma-sm-4 video-preview-container"
                  @click="openVideoPreview(content)"
                >
                  <video
                    :src="getMediaUrl(content)"
                    class="video-preview"
                    muted
                    preload="metadata"
                    @loadedmetadata="onVideoLoaded"
                  />
                  <div class="video-play-overlay">
                    <div class="video-indicator">
                      <v-icon
                        color="white"
                        size="48"
                      >
                        mdi-play-circle
                      </v-icon>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="content.imagePath && content.textKind === PostType.whatsappStatus"
                  class="ma-2 ma-sm-4 d-flex flex-column"
                  :class="isMobile ? 'align-center w-full' : 'align-start'"
                >
                  <WhatsAppStatusPreview
                    :media="content.imagePath"
                    :width="159"
                  />
                </div>
                <r-img
                  v-else-if="content.imagePath"
                  class="ma-2 ma-sm-4 w-full"
                  :src="resize(content.imagePath)"
                  max-width="175"
                  aspect-ratio="1"
                  style="border-radius: 5px; box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2)"
                />
              </div>
              <v-card-text class="pt-2 px-2 px-sm-4 d-flex flex-column flex-md-row gap-05">
                <div v-if="isMobile && content.textKind !== PostType.whatsappStatus">
                  <span> {{ $t('content-creator.scheduled-for-date') }}: </span>
                  <p class="createdAt-content scheduled-for pa-0 ma-0">
                    {{
                      formatDateWithWeekdayName(
                        contentType === ContentType.SCHEDULED_CONTENT
                          ? (content.socialMediaPosts[0]?.scheduledAt ?? content.scheduledAt)
                          : content.postedAt,
                        $i18n.locale
                      )
                    }}
                  </p>
                </div>
                <v-spacer />
                <DownloadPost
                  v-if="
                    contentType === ContentType.SAVED_CONTENT ||
                    contentType === ContentType.SCHEDULED_CONTENT
                  "
                  :class="{ 'col-12': $vuetify.display.smAndDown }"
                  :saved-content-id="content.id"
                  :style="{ 'font-size': $vuetify.display.smAndDown ? 8 + 'px' : '' }"
                  :btn-class="'download-post-btn-in-history'"
                  :text="content.text"
                  :image="content.imagePath"
                  :carousel-images="content.carouselImages || []"
                  :image-source="content.imageSource"
                  :text-kind="content.textKind"
                  :topic="content.slug"
                  :topic-id="content.topicId"
                  :url="content.url"
                  :disabled="disabled"
                  :show-label="!isMobile && content.textKind === PostType.whatsappStatus"
                  @contentSaved="$emit('contentSaved')"
                />
                <v-btn
                  v-if="contentType === ContentType.SCHEDULED_CONTENT"
                  class="edit-post-btn mr-2"
                  :class="{ 'col-12 mb-2': $vuetify.display.smAndDown }"
                  variant="outlined"
                  elevation="0"
                  :style="{ 'font-size': $vuetify.display.smAndDown ? 8 + 'px' : '' }"
                  :disabled="disabled"
                  :aria-label="$t('buttons.content-creator.scheduled-post.edit')"
                  data-product-fruits="edit-scheduled-post-btn"
                  @click="openEditDialog(content)"
                >
                  <v-icon
                    class="mr-2"
                    size="14"
                  >
                    mdi-pencil-outline
                  </v-icon>
                  {{ $t('buttons.content-creator.scheduled-post.edit') }}
                </v-btn>
                <v-btn
                  v-if="contentType === ContentType.SCHEDULED_CONTENT"
                  class="delete-post-btn"
                  :class="{ 'col-12 mb-2': $vuetify.display.smAndDown }"
                  variant="outlined"
                  elevation="0"
                  :style="{ 'font-size': $vuetify.display.smAndDown ? 8 + 'px' : '' }"
                  @click="deletePostDialog(content)"
                >
                  <v-icon
                    class="mr-2"
                    size="14"
                  >
                    mdi-trash-can-outline
                  </v-icon>
                  {{ $t('buttons.content-creator.scheduled-post.delete') }}
                </v-btn>
                <Newsletter
                  v-if="content.textKind === PostType.newsletter"
                  :return-call-landingpage-url="
                    returnCallLandingpage?.customUrl || returnCallLandingpage?.originalUrl
                  "
                  class="ml-md-2"
                  :scheduled-emails-deleted="scheduledEmailsDeleted"
                  :is-master-view="isMasterView"
                  :target-link="isMasterView ? content?.targetLink : null"
                  :individual-link="
                    content?.targetLink === 'individualLink'
                      ? content?.invitations?.[0]?.targetLink
                      : null
                  "
                  :selectedOffices="[]"
                  :publication="isMasterView ? 'all' : null"
                  :content="{
                    ...content,
                    text: content.text,
                    image: content.imagePath,
                  }"
                  @contentSaved="$emit('contentSaved')"
                  @savedScheduledContent="$emit('savedScheduledContent')"
                >
                  <template #reschedule="{ props, emitAction }">
                    <v-btn
                      color="primary"
                      :class="{ 'col-12 mb-2': $vuetify.display.smAndDown }"
                      variant="outlined"
                      :disabled="disabled"
                      elevation="0"
                      size="small"
                      :style="{
                        'font-size': $vuetify.display.smAndDown ? 8 + 'px' : '',
                        width: $vuetify.display.mdAndDown ? 100 + '%' : '',
                      }"
                      v-bind="props"
                      @click="handleRescheduleClick(emitAction)"
                    >
                      <template v-if="contentType === ContentType.SAVED_CONTENT">
                        <v-icon
                          class="mr-2"
                          size="14"
                        >
                          mdi-share-variant
                        </v-icon>
                        <span>{{ $t('content-creator.schedule-email.button') }}</span>
                      </template>
                      <template v-else>
                        <v-icon
                          class="mr-2"
                          size="14"
                        >
                          mdi-calendar-month-outline
                        </v-icon>
                        <span>{{ $t('buttons.content-creator.scheduled-post.reschedule') }}</span>
                      </template>
                    </v-btn>
                  </template>
                </Newsletter>
                <SocialMediaSharing
                  v-else-if="content.textKind !== PostType.whatsappStatus"
                  :content="{
                    ...content,
                    image: content.imagePath,
                  }"
                  is-update
                  :content-type="contentType"
                  :can-post-to-instagram="
                    !!(
                      content.imagePath ||
                      (content.carouselImages && content.carouselImages.length > 0)
                    )
                  "
                  @contentSaved="$emit('contentSaved')"
                  @savedScheduledContent="$emit('savedScheduledContent')"
                >
                  <template v-slot:button="{ props }">
                    <v-btn
                      color="primary"
                      :class="{ 'col-12 mb-2': $vuetify.display.smAndDown }"
                      variant="outlined"
                      :disabled="disabled"
                      elevation="0"
                      size="small"
                      :style="{
                        'font-size': $vuetify.display.smAndDown ? 8 + 'px' : '',
                        width: $vuetify.display.mdAndDown ? 100 + '%' : '',
                      }"
                      v-bind="props"
                      @click="trackPostDialog"
                    >
                      <template v-if="contentType === ContentType.SAVED_CONTENT">
                        <v-icon
                          class="mr-2"
                          size="14"
                        >
                          mdi-share-variant
                        </v-icon>
                        <span>{{ $t('buttons.content-creator.history.share') }}</span>
                      </template>
                      <template v-else>
                        <v-icon
                          class="mr-2"
                          size="14"
                        >
                          mdi-calendar-month-outline
                        </v-icon>
                        <span>{{ $t('buttons.content-creator.scheduled-post.reschedule') }}</span>
                      </template>
                    </v-btn>
                  </template>
                </SocialMediaSharing>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </BlurredBackdropUpselling>
    </v-window-item>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import { formatDate } from '@/lib/dateTime.js'
import { transformToKebabCase } from '@/lib/kebabCase'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { htmlReplace, htmlTruncateAndReplace, textTruncate } from '@/lib/truncate'
import { setQueryParams } from '@/lib/url'
import feature from '@/mixins/feature'
import MaterialPreview from '@/modules/adMaterials/MaterialPreview.vue'
import { ContentType } from '@/modules/contentCreator/creator/enums/ContentType'
import {
  KindName,
  KindToType,
  KindTypeIcon,
  TypeToKind,
} from '@/modules/contentCreator/creator/enums/KindType'
import { PostType } from '@/modules/contentCreator/creator/enums/PostType'
import { ReadableSocialMedia } from '@/modules/contentCreator/creator/enums/SocialMedia'
import { TargetLink } from '@/modules/contentCreator/creator/enums/targetLink.js'
import { formatDateWithWeekdayName } from '@/modules/contentCreator/creator/lib/date'
import {
  getPostStatus,
  getSelectedAccount,
} from '@/modules/contentCreator/creator/lib/publishingStatus.js'
import Newsletter from '@/modules/contentCreator/creator/newsletter/Newsletter.vue'
import RETURN_CALL_LANDINGPAGE from '@/modules/contentCreator/creator/queries/getLandingpageByType.gql'
import GET_SAVED_CONTENT_BY_ID from '@/modules/contentCreator/creator/queries/getSavedContentById.gql'
import DeleteScheduledPost from '@/modules/contentCreator/creator/SocialMedia/DeleteScheduledPost.vue'
import { resolveRecipientSegmentLabel } from '@/modules/leads/enums/Invitation.js'

import DownloadPost from './DownloadPost.vue'
import EditContent from './EditContent.vue'
import GET_COMPANIES_POSTING_INFO from './queries/getCompaniesPostingInfo.gql'
import GET_MASTER_BATCH_POSTS from './queries/getMasterBatchPosts.gql'
import GET_NEWSLETTER_DELIVERY_DETAILS from './queries/getNewsletterDeliveryDetails.gql'
import UPDATE_SAVED_CONTENT from './queries/updateSavedContent.gql'
import UPDATE_SAVED_CONTENT_BATCH from './queries/updateSavedContentBatch.gql'
import PostMetricsRow from './SocialMedia/PostMetricsRow.vue'
import PreviewStep from './SocialMedia/PreviewStep.vue'
import SocialMediaSharing from './SocialMedia/SocialMediaSharing.vue'
import { buildWhatsAppShareUrl } from './WhatsApp/useWhatsAppShare'
import WhatsAppQr from './WhatsApp/WhatsAppQr.vue'
import WhatsAppStatusPreview from './WhatsApp/WhatsAppStatusPreview.vue'

export default {
  components: {
    Newsletter,
    DeleteScheduledPost,
    DownloadPost,
    EditContent,
    BlurredBackdropUpselling,
    SocialMediaSharing,
    ProgressCircular,
    PreviewStep,
    MaterialPreview,
    WhatsAppQr,
    WhatsAppStatusPreview,
    PostMetricsRow,
  },
  props: {
    contentType: {
      type: String,
      default: '',
    },
    savedContents: {
      type: Array,
      default: () => [],
    },
    filterValue: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['contentSaved', 'savedScheduledContent', 'deleteScheduledPost'],
  mixins: [feature],
  data() {
    return {
      hasHandledOpenParam: false,
      loadingPreviewStep: false,
      deleteScheduledPostDialog: false,
      contentForDelete: null,
      scheduledEmailsDeleted: false,
      showEditContentDialog: false,
      selectedContentForEdit: null,
      isUpdating: false,
      KindTypeIcon,
      PostType,
      socialMediaConnections: null,
      showSocialMediaDialog: false,
      selectedContentForPosting: {},
      TypeToKind,
      showViewDetailsDialog: false,
      selectedContentForView: null,
      batchPostsLoading: false,
      newsletterDeliveryDetails: null,
      newsletterRecipientsPage: 1,
      newsletterRecipientsPerPage: 10,
      materialPreviewId: '',
      selectedMaterialForPreview: null,
    }
  },
  computed: {
    /** Total rows for newsletter history table (client-side pagination in PreviewStep). */
    newsletterTableItemsLength() {
      if (!this.isNewsletter || !this.selectedContentForView) return undefined
      if (!this.isMasterView) {
        const n = this.newsletterDeliveryDetails?.recipients?.length ?? 0
        return n > 0 ? n : undefined
      }
      const n = this.selectedContentForView.invitations?.length ?? 0
      return n > 0 ? n : undefined
    },
    isNewsletter() {
      return this.selectedContentForView?.textKind === PostType.newsletter
    },
    resolvedContent() {
      return this.isNewsletter ? this.newsletterPreviewContent : this.previewContentData
    },

    resolvedOffices() {
      return this.isNewsletter ? this.newsletterOfficesData : this.previewOfficesData
    },

    resolvedShowPostColumn() {
      return this.isNewsletter ? false : this.hasAnyPermalink
    },

    resolvedSubmitText() {
      return this.isNewsletter ? this.$t('labels.proceed') : ''
    },

    resolvedPreviewTitle() {
      if (this.isNewsletter) {
        return this.contentType === ContentType.SCHEDULED_CONTENT
          ? this.$t('content-creator.newsletter-details.email-preview')
          : this.$t('content-creator.sent-email')
      }

      return this.contentType === ContentType.SCHEDULED_CONTENT
        ? this.$t('content-creator.preview-step.preview.title')
        : this.$t('content-creator.preview-step.published-post.title')
    },
    TargetLink() {
      return TargetLink
    },
    isMobile() {
      return this.$vuetify.display.smAndDown
    },
    ContentType() {
      return ContentType
    },
    cssProps() {
      return {
        '--primary-color': this.$vuetify.theme.global?.current?.colors.primary,
      }
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    newsletterPreviewContent() {
      if (!this.selectedContentForView) return {}
      return {
        topicName: this.selectedContentForView.topicName || '',
        image: this.selectedContentForView.imagePath,
        text: (() => {
          const rawText = this.selectedContentForView?.text || ''
          const lines = rawText.split('\n').filter(Boolean)
          const body = lines.slice(1).join('\n')
          return htmlReplace(body)
        })(),
        subject: this.selectedContentForView?.text
          ? this.selectedContentForView.text.split('\n').filter(Boolean)[0]
          : '',
        date: this.selectedContentForView.scheduledAt || this.selectedContentForView.postedAt,
        textKind: PostType.newsletter,
        publication: this.selectedContentForView?.publication,
      }
    },
    previewContentData() {
      if (!this.selectedContentForView) return {}
      const isScheduled = this.contentType === ContentType.SCHEDULED_CONTENT
      return {
        topicName: this.selectedContentForView.topicName || '',
        image: this.selectedContentForView.imagePath,
        text: this.selectedContentForView.text,
        date: isScheduled
          ? (this.selectedContentForView.socialMediaPosts?.[0]?.scheduledAt ??
            this.selectedContentForView.scheduledAt)
          : this.selectedContentForView.postedAt,
        publication: 'head',
      }
    },
    hasAnyPermalink() {
      if (!this.selectedContentForView || !this.selectedContentForView.posts) return false
      const posts = this.selectedContentForView.posts || []
      return posts.some((post) => {
        const socialMediaPosts = post?.socialMediaPosts || []
        return socialMediaPosts.some((sm) => sm.permalink != null && sm.permalink.trim() !== '')
      })
    },
    newsletterStatistics() {
      const stats = [
        {
          icon: 'mdi-account-group',
          text: `${this.$t('content-creator.newsletter-details.recipient-segment')}: ${this.resolveRecipientSegmentLabel(this.selectedContentForView?.recipientSegment)}`,
        },
        {
          icon: 'mdi-calendar-month-outline',
          text: formatDate(
            new Date(
              this.selectedContentForView?.postedAt || this.selectedContentForView?.scheduledAt
            ),
            'dateLong',
            this.$i18n.locale
          ),
        },
        {
          icon: 'mdi-calendar-clock',
          text: formatDate(
            new Date(
              this.selectedContentForView?.postedAt || this.selectedContentForView?.scheduledAt
            ),
            'timeOnly',
            this.$i18n.locale
          ),
        },
        this.isMasterView && {
          icon: 'mdi-badge-account-horizontal-outline',
          text: `${this.selectedContentForView?.totalRecipients} ${this.$t('content-creator.newsletter-details.recipients')}`,
        },
        !this.isMasterView &&
          !this.selectedContentForView?.scheduledAt &&
          this.selectedContentForView?.textKind === PostType.newsletter && {
            icon: 'mdi-badge-account-horizontal-outline',
            text: `${this.selectedContentForView?.totalRecipients || this.selectedContentForView?.invitationIds.length} ${this.$t('content-creator.newsletter-details.recipients')}`,
          },
      ].filter(Boolean)
      const d = this.newsletterDeliveryDetails
      if (!this.isMasterView && d) {
        stats.push(
          this.contentType !== ContentType.SCHEDULED_CONTENT &&
            d.sent > 0 && {
              icon: 'mdi-check-circle-outline',
              text: this.$t('content-creator.newsletter-details.delivery-summary-sent', {
                n: d.sent,
              }),
            },
          this.contentType !== ContentType.SCHEDULED_CONTENT &&
            d.failed > 0 && {
              icon: 'mdi-alert-circle-outline',
              text: this.$t('content-creator.newsletter-details.delivery-summary-failed', {
                n: d.failed,
              }),
            },
          this.contentType !== ContentType.SCHEDULED_CONTENT &&
            d.pending > 0 && {
              icon: 'mdi-clock-outline',
              text: this.$t('content-creator.newsletter-details.delivery-summary-pending', {
                n: d.pending,
              }),
            },
          this.contentType === ContentType.SCHEDULED_CONTENT &&
            d.pending > 0 && {
              icon: 'mdi-badge-account-horizontal-outline',
              text: `${this.$t('content-creator.newsletter-details.recipients')} ${d.pending}`,
            }
        )
      }
      return stats.filter(Boolean)
    },
    isPostVideoEnabled() {
      return this.getFeature(this.featureNames.CONTENT_CREATOR).config.postVideo
    },
    showVideoUpsell() {
      return (
        this.selectedMaterialForPreview?.id &&
        this.selectedMaterialForPreview?.assetType === 'VIDEO' &&
        !this.isPostVideoEnabled
      )
    },
    newsletterOfficesData() {
      if (
        !this.selectedContentForView ||
        this.selectedContentForView?.textKind !== PostType.newsletter
      ) {
        return []
      }

      const perPage = this.newsletterRecipientsPerPage
      let full = []

      if (!this.isMasterView && this.newsletterDeliveryDetails?.recipients?.length) {
        full = this.newsletterDeliveryDetails.recipients.map((r) => ({
          name: r.leadName || '—',
          recipientEmail: r.leadEmail || '',
          deliveryStatus: r.deliveryStatus || 'pending',
          deliveryError: r.deliveryError || null,
        }))
      } else if (this.isMasterView && this.selectedContentForView?.invitations?.length) {
        full = this.selectedContentForView.invitations.map((invitation) => ({
          name: invitation.name,
          targetLink: invitation.targetLink,
          socialMediaCount: invitation.recipientCount?.toString(),
          failedCount: invitation.failedCount ?? 0,
          failedDeliveryError: invitation.failedDeliveryError || null,
        }))
      } else {
        return []
      }

      if (!this.newsletterTableItemsLength || full.length <= perPage) {
        return full
      }
      const start = (this.newsletterRecipientsPage - 1) * perPage
      return full.slice(start, start + perPage)
    },
    previewOfficesData() {
      if (!this.selectedContentForView) return []

      const posts = this.selectedContentForView.posts || []
      const companies = this.getCompaniesPostingInfo || []
      const companyIds = [...new Set(posts.map((p) => p.companyId).filter(Boolean))]
      return companyIds.map((companyId) => {
        const company = companies.find((c) => c.id === companyId)
        const postForCompany = posts.find((p) => p.companyId === companyId)
        const socialMediaPosts = postForCompany?.socialMediaPosts || []

        return {
          name: this.createOfficeName(company, companyId),
          socialMediaCount: this.getPublishingTargets(
            company,
            socialMediaPosts,
            postForCompany?.hasActiveConnection
          ),
          targetLink: postForCompany?.url || this.selectedContentForView.url || '',
          postUrl: this.getPostUrls(socialMediaPosts),
        }
      })
    },
  },
  watch: {
    '$route.query.open'(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.hasHandledOpenParam = false
      }
    },
    savedContent(newValue) {
      if (!newValue || !this.$route.query.open || this.hasHandledOpenParam) return

      this.hasHandledOpenParam = true
      this.openViewDetailsDialog(newValue)
    },
  },
  methods: {
    htmlReplace,
    htmlTruncateAndReplace,
    createOfficeName(company, companyId) {
      if (!company) return companyId

      const { firstname = '', lastname = '' } = company.ambassador || {}
      const ambassadorName = [firstname, lastname].filter(Boolean).join(' ')
      const { city = '' } = company.address || {}

      return [company.name || companyId, ambassadorName, city].filter(Boolean).join(' - ')
    },
    getPublishingTargets(company, socialMediaPosts, hasActiveConnection = true) {
      return socialMediaPosts.map((post) => {
        const platform = post.type?.toUpperCase()
        const account = getSelectedAccount(company, platform)

        return {
          platform,
          username: account?.name || account?.username || '—',
          postStatus: getPostStatus(post, account, this.contentType, hasActiveConnection),
          error: post?.failureReason || null,
        }
      })
    },
    getPostUrls(socialMediaPosts) {
      const urls = socialMediaPosts
        .map((sm) =>
          sm.permalink?.trim()
            ? {
                platform: sm.type,
                url: sm.permalink.trim(),
              }
            : null
        )
        .filter(Boolean)

      return urls.length ? urls : null
    },
    resolveRecipientSegmentLabel(key) {
      return resolveRecipientSegmentLabel(key, this.$t)
    },
    getRecipientsCount(content) {
      if (this.contentType === ContentType.SAVED_CONTENT) {
        return content.invitationIds?.length
      }
      return content?.totalRecipients || 0
    },
    isVideo(content) {
      if (!content) return false

      if (content.imagePath && content.imagePath.match(/\.(mp4|mov|avi|wmv|webm)(\?|$)/i)) {
        return true
      }

      return false
    },
    getMediaUrl(content) {
      return content.imagePath || content.url
    },
    whatsAppQrUrl(content) {
      return `${window.location.origin}${buildWhatsAppShareUrl(content.text || '', content.imagePath || '')}`
    },
    onVideoLoaded(event) {
      // * Set current time to 1 second to show a preview frame
      const videoElement = event.target
      if (videoElement) {
        videoElement.currentTime = 1
      }
    },
    openVideoPreview(content) {
      this.selectedMaterialForPreview = {
        id: content.id,
        displayName: content.textKind || 'Video',
        url: this.getMediaUrl(content),
        previewUrl: this.getMediaUrl(content),
        assetType: 'VIDEO',
        createdAt: content.postedAt || content.scheduledAt || '',
      }
      this.materialPreviewId = content.id
      this.$tracking.event('Content History', 'Clicked', 'Video-Preview')
    },
    handleRescheduleClick(emitAction) {
      this.trackPostDialog()
      const action =
        this.contentType === this.ContentType.SAVED_CONTENT ? 'selectContacts' : 'reschedule'
      emitAction(action)
    },
    resize(url) {
      return setQueryParams(url, { h: 350, w: 350 })
    },
    transformToKebabCase,
    formatDateWithWeekdayName,
    activateContentCreatorTab() {
      this.$tracking.event('Content History', 'Clicked', 'Create More Posts')
      this.$router.push({ query: { section: 'creator' } })
    },
    deletePostDialog(content) {
      this.deleteScheduledPostDialog = true
      this.contentForDelete =
        content.textKind === PostType.newsletter
          ? { ...content, type: PostType.newsletter.toUpperCase() }
          : content
    },
    onSocialMediaIconClick(permalink) {
      if (permalink) {
        window.open(permalink, '_blank')
      }
      this.$tracking.event('Content History', 'Clicked', 'Link-to-Post')
    },
    // Channel display name from a SocialMediaType. KindName is the utm_source map,
    // so it is only the fallback for kinds that are not social channels.
    platformName(type) {
      return ReadableSocialMedia[type] || KindName[TypeToKind[type]]
    },
    // Same label, reached from a text kind instead of a SocialMediaType.
    platformNameForKind(kind) {
      return ReadableSocialMedia[KindToType[kind]] || KindName[kind]
    },
    getSocialMediaPostStatusLabel(post) {
      if (post.hasFailed) {
        return post.failureReason
          ? `${this.$t('social-media.posting.status-failed')}: ${post.failureReason}`
          : this.$t('social-media.posting.status-failed')
      }
      if (!post.completedAt && !post.externalPostId && !post.permalink)
        return this.$t('social-media.posting.status-pending')
      return this.platformName(post.type)
    },
    textTruncate,
    trackPostDialog() {
      this.$tracking.event('Content History', 'Clicked', 'Content-Repurpose')
    },
    openEditDialog(content) {
      this.selectedContentForEdit = content
      this.showEditContentDialog = true
      this.$tracking.event('Content History', 'Clicked', 'Edit-Scheduled-Post')
    },
    closeEditDialog() {
      this.showEditContentDialog = false
      this.selectedContentForEdit = null
    },
    async updateScheduledPostText(updatedText) {
      if (!this.selectedContentForEdit) {
        this.closeEditDialog()
        return
      }

      try {
        if (this.isUpdating) return
        this.isUpdating = true
        // Update the saved content with the new text using the backend API
        if (this.isMasterView && this.selectedContentForEdit.postingBatchId) {
          await this.$apollo.mutate({
            mutation: UPDATE_SAVED_CONTENT_BATCH,
            variables: {
              input: {
                postingBatchId: this.selectedContentForEdit.postingBatchId,
                text: updatedText,
              },
            },
          })
        } else {
          await this.$apollo.mutate({
            mutation: UPDATE_SAVED_CONTENT,
            variables: {
              input: {
                id: this.selectedContentForEdit.id,
                text: updatedText,
              },
            },
          })
        }

        // Update the text in the local content object (do this before closing dialog)
        if (this.selectedContentForEdit) {
          this.selectedContentForEdit.text = updatedText
        }

        // Emit event to refresh the content list
        this.$emit('savedScheduledContent')
        this.$tracking.event('Content History', 'Updated', 'Scheduled-Post-Text')

        // Show success message
        showSnackbarMessage('success', this.$t('content-creator.edit-text-success'))

        // Close dialog after successful update
        this.closeEditDialog()
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'SavedContentsTab',
            action: 'updateScheduledPostText',
          },
          extra: {
            contentId: this.selectedContentForEdit?.id,
            textKind: this.selectedContentForEdit?.textKind,
          },
        })
        // Show user-friendly error without leaking internal details
        showSnackbarMessage('error', this.$t('content-creator.edit-text-error'))
        this.closeEditDialog()
      } finally {
        this.isUpdating = false
      }
    },
    getOfficesCount(content) {
      if (content.textKind === PostType.newsletter) {
        return content?.invitations?.length || 0
      }
      // When the detail dialog has loaded the batch posts (getMasterBatchPosts),
      // derive counts from them so the summary header matches the per-account
      // table below it (and the publishing success modal, which uses the same
      // source). Both include not-connected offices; the backend list counts
      // (officeCount) cover connected offices only.
      if (content.posts?.length) {
        const uniqueCompanyIds = new Set(
          content.posts.map((post) => post.companyId).filter(Boolean)
        )
        return uniqueCompanyIds.size
      }
      if (content.officeCount != null) return content.officeCount
      return 0
    },
    getAccountsCount(content) {
      if (content.posts?.length) {
        return content.posts.reduce((total, post) => {
          return total + (post.socialMediaPosts?.length || 0)
        }, 0)
      }
      if (content.accountCount != null) return content.accountCount
      return 0
    },
    getPlatformIcon(content) {
      if (content.socialMediaPosts && content.socialMediaPosts.length > 0) {
        const type = content.socialMediaPosts[0].type
        return KindTypeIcon[TypeToKind[type]] || null
      }
      return KindTypeIcon[content.textKind] || null
    },
    getPlatformName(content) {
      if (content.socialMediaPosts && content.socialMediaPosts.length > 0) {
        const type = content.socialMediaPosts[0].type
        return this.platformName(type) || type
      }
      return this.platformNameForKind(content.textKind) || content.textKind
    },
    showViewDetailsButton(content) {
      if (content.textKind === PostType.newsletter) {
        return (
          content.invitationIds?.length > 0 ||
          content.invitations?.length > 0 ||
          content.scheduledAt
        )
      }
      return (
        this.isMasterView &&
        (content.officeCount > 0 ||
          content.postingBatchId ||
          content.posts?.length > 0 ||
          content.socialMediaPosts?.length > 0 ||
          content.invitations?.length > 0)
      )
    },
    closeViewDetailsDialog() {
      this.showViewDetailsDialog = false
      this.selectedContentForView = null
      this.batchPostsLoading = false
      this.newsletterDeliveryDetails = null
      this.newsletterRecipientsPage = 1
      if (this.$route.query.open) {
        const query = { ...this.$route.query }
        delete query.open
        this.$router.replace({ query })
      }
    },
    onNewsletterTableItemsPerPage() {
      this.newsletterRecipientsPage = 1
    },
    async openViewDetailsDialog(content) {
      this.$tracking.event('Content History', 'Clicked', 'View-Details', this.isMasterView)
      this.newsletterDeliveryDetails = null
      this.newsletterRecipientsPage = 1

      const needsBatchPosts =
        this.isMasterView && content?.postingBatchId && content?.textKind !== PostType.newsletter

      if (needsBatchPosts) {
        this.selectedContentForView = content
        this.batchPostsLoading = true
        this.showViewDetailsDialog = true
        try {
          const { data } = await this.$apollo.query({
            query: GET_MASTER_BATCH_POSTS,
            variables: { input: { postingBatchId: content.postingBatchId } },
            fetchPolicy: 'network-only',
          })
          this.selectedContentForView = {
            ...content,
            posts: data?.getMasterBatchPosts ?? [],
          }
        } catch (e) {
          Sentry.captureException(e, {
            tags: { component: 'SavedContentsTab', action: 'getMasterBatchPosts' },
            extra: { postingBatchId: content.postingBatchId },
          })
          this.selectedContentForView = { ...content, posts: [] }
        } finally {
          this.batchPostsLoading = false
        }
      } else {
        let viewContent = content
        if (
          this.isMasterView &&
          content?.textKind !== PostType.newsletter &&
          !content?.postingBatchId
        ) {
          viewContent = {
            ...content,
            posts: [
              {
                companyId: content.companyId,
                textKind: content.textKind,
                url: content.url,
                postingBatchId: content.postingBatchId,
                socialMediaPosts: content.socialMediaPosts || [],
              },
            ],
          }
        }
        this.selectedContentForView = viewContent
        this.showViewDetailsDialog = true
      }

      if (content?.textKind === PostType.newsletter && !this.isMasterView && content?.id) {
        try {
          this.loadingPreviewStep = true
          const { data } = await this.$apollo.query({
            query: GET_NEWSLETTER_DELIVERY_DETAILS,
            variables: {
              input: {
                savedContentId: content.id,
                isMasterView: false,
              },
            },
            fetchPolicy: 'network-only',
          })
          this.newsletterDeliveryDetails = data?.getNewsletterDeliveryDetails ?? null
        } catch (e) {
          Sentry.captureException(e, {
            tags: { component: 'SavedContentsTab', action: 'getNewsletterDeliveryDetails' },
          })
          this.newsletterDeliveryDetails = null
        } finally {
          this.loadingPreviewStep = false
        }
      }
      if (this.isMasterView && content?.textKind === PostType.newsletter) {
        return
      }
      // Refetch companies posting info when dialog opens
      // Use $nextTick to ensure reactivity has propagated before refetching
      if (
        this.isMasterView &&
        content?.topicId &&
        this.$auth?.user?.companyId &&
        this.$apollo.queries.getCompaniesPostingInfo
      ) {
        this.$nextTick(() => {
          this.$apollo.queries.getCompaniesPostingInfo.refetch()
        })
      }
    },
  },
  apollo: {
    savedContent: {
      query: GET_SAVED_CONTENT_BY_ID,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          input: {
            savedContentId: String(this.$route.query.open) || '',
          },
        }
      },
      skip() {
        return this.hasHandledOpenParam || !this.$route?.query?.open
      },
    },
    returnCallLandingpage: {
      query: RETURN_CALL_LANDINGPAGE,
      variables() {
        return {
          input: {
            landingpageType: 'RETURN_CALL',
          },
        }
      },
    },
    getCompaniesPostingInfo: {
      query: GET_COMPANIES_POSTING_INFO,
      variables() {
        return {
          input: {
            parentId: this.$auth?.user?.companyId || '',
            topicId: this.selectedContentForView?.topicId || '',
          },
        }
      },
      skip() {
        return (
          !this.isMasterView ||
          !this.selectedContentForView?.topicId ||
          this.$auth.loading ||
          !this.$auth.isAuthenticated ||
          !this.$auth?.user?.companyId
        )
      },
      update(data) {
        return data?.getCompaniesPostingInfo || []
      },
      error(error) {
        console.error('Failed to fetch companies posting info:', error)
        Sentry.captureException(error, {
          tags: {
            component: 'SavedContentsTab',
            action: 'getCompaniesPostingInfo',
          },
          extra: {
            topicId: this.selectedContentForView?.topicId,
            companyId: this.$auth?.user?.companyId,
          },
        })
      },
    },
  },
}
</script>

<style scoped>
.whatsapp-qr-wrapper {
  width: 77px;
  height: 77px;
  border-radius: 10px;
  border: 1px solid rgb(var(--v-theme-ganz-hell-turkis));
  overflow: hidden;
  opacity: 1;
}

.square-icon {
  width: 25px; /* Ensure the width matches the size prop */
  height: 25px; /* Ensure the height matches the size prop */
  border-radius: 5px; /* Adjust this value for the desired roundness */
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.v-timeline-item) {
  margin-left: 200px;
  margin-right: 200px;
}

:deep(.v-timeline-divider__before) {
  background: rgba(0, 0, 0, 0.12);
}

@media (max-width: 960px) {
  :deep(
    .v-timeline--vertical.v-timeline.v-timeline--side-end .v-timeline-item .v-timeline-item__body
  ) {
    grid-column: 1;
    padding-inline-start: 0;
  }
  :deep(.v-timeline--vertical.v-timeline) {
    row-gap: 10px;
  }
  :deep(.v-timeline-divider) {
    position: absolute;
    grid-column: auto !important;
    right: 80%;
    z-index: -1;
  }
}

@media (min-width: 962px) and (max-width: 1740px) {
  :deep(.v-timeline-item) {
    margin-left: 60px;
    margin-right: 60px;
  }
}
@media (min-width: 0) and (max-width: 1540px) {
  :deep(.v-timeline-item) {
    margin-left: 0;
    margin-right: 0;
  }
}

.timeline-avatar {
  background-color: white;
}

.social-post-icon-wrapper {
  position: relative;
  display: inline-block;
}

.post-status-badge {
  position: absolute;
  bottom: -6px;
  right: -6px;
  background: white;
  border-radius: 50%;
}

.createdAt {
  color: #99a1b7;
  font-weight: 600;
}

.createdAt-content {
  color: #071437;
  font-weight: 500;
}

.table-row {
  border-bottom: 1px solid #e0e0e0;
}

.pointer {
  cursor: pointer;
}

.tooltip-compact .tooltip-content,
:deep(.v-alert) {
  max-width: 500px;
}

:deep(.tooltip-class.v-overlay__content) {
  background: unset !important;
}

.tooltip-compact .tooltip-content :deep(.v-icon) {
  color: #fff;
}

@media (max-width: 600px) {
  .tooltip-compact {
    max-width: 100%;
  }
}

.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: transparent;
}

.th-downloaded {
  width: 50px;
}

.v-btn:not(.v-btn--round).v-size--default {
  height: 30px;
  padding: 0 8px;
  font-size: 10px;
}

.avatar-icon-download {
  color: white;
  font-size: 22px;
}
:deep(.v-timeline-item__dot) {
  width: 48px;
  height: 48px;
}

:deep(.v-timeline-item__dot .v-timeline-item__inner-dot) {
  margin: 0;
  width: 48px;
  height: 48px;
}

.scheduled-for {
  color: var(--primary-color);
}

.master-view-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.master-view-info .v-btn {
  text-transform: uppercase;
  font-weight: 600;
}

.master-view-info .view-details-btn {
  width: fit-content;
  align-self: flex-start;
}

:deep(.social-media-dialog) {
  border-radius: 4px;
  overflow: hidden;
}

:deep(.social-media-dialog .v-card) {
  overflow: hidden;
  max-width: 100%;
}

:deep(.social-media-dialog .v-toolbar) {
  box-shadow: none;
}

.preview-step-wrapper {
  margin: 1.5rem;
  overflow-x: hidden;
  min-width: 0;
  width: calc(100% - 3rem);
  max-width: calc(100% - 3rem);
}

.preview-step-wrapper__newsletter :deep(.preview-step-content) {
  grid-template-columns: 1fr 1fr;
}
.preview-step-wrapper__newsletter :deep(.preview-step__preview-post .v-responsive) {
  max-width: 600px;
  max-height: 350px;
  width: 100%;
  height: auto;
  object-fit: contain;
  margin-bottom: 20px;
  display: block;
}
.preview-step-wrapper__newsletter :deep(.preview-step__preview-post p span button) {
  background-color: rgb(var(--v-theme-primary)) !important;
}

:deep(.preview-step-wrapper .preview-step) {
  height: 100%;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

:deep(.preview-step-wrapper .preview-step-content) {
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

:deep(.preview-step-wrapper .preview-step-content__summary) {
  min-width: 0;
  overflow-x: hidden;
}

:deep(.preview-step-wrapper .preview-step__preview) {
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

.dialog-title {
  font-size: 22px;
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  flex: unset;
}

.delete-post-btn,
.edit-post-btn {
  padding: 8px !important;
}

.clickable-text {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-text:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.card-create-more-posts {
  width: fit-content;
}

.carousel-indicator-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: fit-content;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  z-index: 2;
}

.carousel-count {
  color: white;
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 600px) {
  :deep(.v-timeline-item__divider) {
    display: none !important;
  }
  :deep(.v-timeline--dense .v-timeline-item__body) {
    max-width: 100% !important;
  }
}

.video-preview-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 175px;
  height: 175px;
  border-radius: 5px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  background-color: #000;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.whatsapp-video-preview-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 159px;
  aspect-ratio: 9 / 16;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  background-color: #1a1a1a;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.whatsapp-video-preview-container:hover {
  transform: scale(1.02);
}

.video-preview-container:hover {
  transform: scale(1.02);
}

.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.whatsapp-video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.video-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
}

.video-indicator {
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}
</style>
